<template lang="pug">
.members
  .seats-increase-warning-box.dialog-warning.blue.align-items-center(v-if="organization.show_per_seat_ui")
    i.d-flex.align-items-center.hi.hi-info-filled.hi-12.text-blue.pt-0

    div
      | Moving Project viewers to Users or Managers will move them from a free seat to a paid seat, and may increase the number of paid seats on your plan.&nbsp;
      a.text-nowrap(href="https://support.hubstaff.com/per-seat-pricing/")
        | Learn more

  #manager.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="manager"
        multiple
        searchable
        :unclearable-ids="teamMemberIds"
        :fetcher="membersFetcher"
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Managers"
        placeholder="Select managers"
        helper-text="Oversees and manages the project"
        :disabled="disabled"
      )

  #user.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="user"
        multiple
        searchable
        :unclearable-ids="teamMemberIds"
        :fetcher="membersFetcher"
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Users"
        placeholder="Select users"
        helper-text="Works on the project, will not see other users (most common)"
        tooltip="Users who have member limits set within this project will not be removed."
      )

  #viewer.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="viewer"
        multiple
        searchable
        :unclearable-ids="teamMemberIds"
        :fetcher="membersFetcher"
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Viewers"
        placeholder="Select viewers"
        helper-text="Can view team reports for this project"
        :disabled="disabled"
      )
</template>

<script>
import ZoneSelect from 'zone/Select.vue';
import { ProjectsResource } from '~/resources';
import { mapState, mapActions } from 'vuex';
import { createPaginatedSelectZoneFetcher } from '~/utils/paginated_select_zone_fetcher';

const MEMBER_PAGE_SIZE = 50;

export default {
  name: 'ProjectDialogTabMembers',
  components: { ZoneSelect },
  computed: {
    ...mapState({
      project: (state) => state.project,
      allMembers: (state) => ({ ...state.membersMap }),
      organization: (state) => state.organization,
      disabled: (state) => !state.organization.features.manager_roles.available,
      teamMemberIds: (state) => state.project.team_member_ids || [],
    }),
    projectId() {
      return this.project?.id || null;
    },
    organizationId() {
      return this.organization.id;
    },
    membersFetcher() {
      const organizationId = this.organizationId;
      const projectId = this.projectId;

      return createPaginatedSelectZoneFetcher({
        pageSize: MEMBER_PAGE_SIZE,
        disableSelectedDisabledIds: false,
        mapOption: (item) => ({
          avatar: item.avatar,
          ...(item.removed ? { description: 'Removed' } : {}),
        }),
        request: (payload) =>
          ProjectsResource.members(
            { organization_id: organizationId },
            {
              ...payload,
              resource_id: projectId,
              ...(this.project.id ? { all_visible: false } : { restricted_lead_members: true }),
            },
            { abortReload: true },
          ),
      });
    },

    ...Object.fromEntries(
      ['manager', 'user', 'viewer'].map((role) => [
        role,
        {
          get() {
            return [...this.allMembers[role].values()];
          },
          set(items) {
            this.updateMembers({ items, role });
          },
        },
      ]),
    ),
  },
  methods: {
    ...mapActions(['updateMembers']),
  },
};
</script>

<style scoped>
.project-members-zone-select :deep(.relative.font-regular > button) {
  min-height: 42px;
  height: 42px;
}
</style>
